import React, { useContext } from 'react'
import { Card, Col, DateFormat, enhancedFetch, JourneyAppContext, RadioInput, Row, useAlert } from 'journey-ui'
import { ItemPageContext } from '../../contexts/ItemPageContext'
import { calculateItemToppingPrice, isIncludedTopping, money, sum } from '../../helpers'
import { CardTitle } from '../../pages/item/[slug]'

interface Props {

}

export const SizeSelection: React.FC<Props> = () => {
  const { form, item, orderItem, setItemPageJson, itemPageJson, setForm, setBusy, hasFreeAvailable, rules } = useContext(ItemPageContext)
  const { setAlert } = useContext(JourneyAppContext)

  const onSizeClick = async (size: ItemPageJsonSize) => {
    if (size.unavailable_until_at) {
      setAlert(
        <div className="flex space-x-1">
          <span>Sorry this size is unavailable until</span>
          <DateFormat format="M/d/yy h:mma" value={size.unavailable_until_at} />
        </div>,
      )
      return false
    }

    setBusy(true)

    try {
      /* Main purpose of this is to refetch the tiles upon size change as some toppings
      * may be unavailable at certain sizes. */
      const changeSizeResp = await enhancedFetch<ChangedSizeResp>('/api/item/change-size', {
        method: 'POST',
        body: {
          size_hash_id: size.hash_id,
          item_hash_id: orderItem?.hash_id,
          toppings: form.toppings.map((t) => ({ hash_id: t.hash_id })),
        },
      })

      setItemPageJson(changeSizeResp.item_page)

      const previousSize = item.sizes.find((s) => s.name === form.size_name)

      setForm((prevForm) => {
        const newToppings: ItemTopping[] = changeSizeResp.new_toppings.map((sectionTopping) => {
          let newPrice = sectionTopping.price

          /* If we have the same current topping already picked, but just a different size. */
          const currentTopping = prevForm.toppings.find((t) => t.name === sectionTopping.name && t.portion_name === sectionTopping.portion_name)

          /* If our previous topping was selected as free, make sure the price reflects that still. */
          if ((currentTopping && currentTopping.free_quantity > 0) || isIncludedTopping(sectionTopping, itemPageJson)) {
            newPrice = 0
          }

          return {
            ...sectionTopping,
            is_included: isIncludedTopping(sectionTopping, changeSizeResp.item_page),
            free_quantity: currentTopping?.free_quantity ?? 0,
            price: newPrice,
          }
        })

        const newToppingPrice = sum(newToppings.map((t) => t.price)) * prevForm.quantity
        const previousToppingPrice = sum(prevForm.toppings.map((t) => t.price)) * prevForm.quantity
        const previousSizePrice = (previousSize.price * prevForm.quantity)
        const newSizePrice = (size.price * prevForm.quantity)

        return {
          ...prevForm,
          size_name: size.name,
          price: prevForm.price
            - previousSizePrice
            + newSizePrice
            - previousToppingPrice
            + newToppingPrice,
          toppings: newToppings,
        }
      })
    } catch (error) {
      setBusy(false)
      throw error
    }

    setBusy(false)
  }

  return (
    <Card>
      <CardTitle>Select A Size</CardTitle>
      <Row className="justify-start">
        {item.sizes.map((size) => (
          <Col className="flex items-center" key={size.hash_id} lg={item.sizes.length >= 4 ? 3 : 6} md={4} sm={6} xs={6}>
            <RadioInput
              name="size_name"
              className="m-0"
              checkedValue={size.name}
              label={`${size.name} - ${money(size.price)}`}
              onClick={() => onSizeClick(size)}
            />
          </Col>
        ))}
      </Row>
    </Card>
  )
}
